import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Search, Check, ChevronDown, X } from 'lucide-react';

export interface SearchSelectOption {
  id: string;
  label: string;
}

interface Props {
  options: SearchSelectOption[];
  /** Selected id, or undefined for "nothing chosen". */
  value?: string;
  onSelect: (id: string | undefined) => void;
  /** Trigger text when nothing is selected. */
  placeholder?: string;
  /** Adds a "clear" row at the top of the list, e.g. "Any client". */
  clearLabel?: string;
  /** Word used in the search box and the empty state ("Search client"). */
  noun?: string;
  testid: string;
  disabled?: boolean;
}

/**
 * Searchable single-select over plain `{ id, label }` options — the same
 * interaction as `booking/ArtistPicker` (trigger, filter box, listbox,
 * click-outside), but not tied to the Artist type, so it works for any list
 * long enough that a native `<select>` stops being usable.
 *
 * Keyboard: type to filter, ↑/↓ to move, Enter to pick, Escape to close.
 */
export default function SearchSelect({
  options,
  value,
  onSelect,
  placeholder = 'Select…',
  clearLabel,
  noun = 'option',
  testid,
  disabled,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    // Match on every word typed, in any order, so "harper 12" finds
    // "Mon 4 May, 12:30 · Harper Nguyen · Asha (confirmed)".
    const words = q.split(/\s+/);
    return options.filter((o) => {
      const haystack = o.label.toLowerCase();
      return words.every((w) => haystack.includes(w));
    });
  }, [options, query]);

  // Keep the highlight inside the filtered list as it shrinks.
  useEffect(() => setCursor(0), [query]);

  const selected = value ? options.find((o) => o.id === value) : undefined;

  const choose = (id: string | undefined) => {
    onSelect(id);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="relative min-w-0" ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setOpen((v) => !v);
          setQuery('');
        }}
        className={clsx(
          'w-full flex items-center justify-between gap-2 bg-ink-black border rounded-md px-2 py-1.5 text-left text-sm transition disabled:opacity-40 disabled:cursor-not-allowed',
          selected ? 'border-gold-bright/50 text-cream' : 'border-gold-dim/40 text-cream-faint',
          !disabled && 'hover:border-gold-bright/60',
        )}
        data-testid={testid}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate">{selected?.label ?? placeholder}</span>
        {/* Only offer a clear on fields that HAVE an empty state. The beat picker
            always has a beat selected, so an × there would promise something it
            cannot do. */}
        {selected && clearLabel ? (
          <X
            size={12}
            className="text-cream-faint shrink-0 hover:text-blood"
            role="button"
            aria-label={`Clear ${noun}`}
            data-testid={`${testid}-clear`}
            onClick={(e) => {
              e.stopPropagation();
              choose(undefined);
            }}
          />
        ) : (
          <ChevronDown size={13} className="text-cream-faint shrink-0" />
        )}
      </button>

      {open ? (
        <div
          className="absolute z-20 mt-1 w-full panel-dark py-1 shadow-ink-soft"
          data-testid={`${testid}-menu`}
        >
          <div className="relative px-2 pb-1">
            <Search size={12} className="absolute left-4 top-1/2 -translate-y-1/2 text-cream-faint" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${noun}`}
              aria-label={`Search ${noun}`}
              data-testid={`${testid}-search`}
              className="w-full bg-ink-black border border-gold-dim/40 rounded-md pl-7 pr-2 py-1.5 text-sm text-cream focus:outline-none focus:border-gold-bright"
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setCursor((c) => Math.min(c + 1, results.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setCursor((c) => Math.max(c - 1, 0));
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  const pick = results[cursor];
                  if (pick) choose(pick.id);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setOpen(false);
                }
              }}
            />
          </div>
          <ul className="max-h-56 overflow-y-auto" role="listbox">
            {clearLabel ? (
              <li>
                <button
                  type="button"
                  onClick={() => choose(undefined)}
                  className="w-full text-left px-3 py-2 text-sm text-cream-soft hover:bg-ink-soft/60 transition"
                  data-testid={`${testid}-option-any`}
                >
                  {clearLabel}
                </button>
              </li>
            ) : null}
            {results.length === 0 ? (
              <li className="px-3 py-2 font-serif text-sm text-cream-soft">No {noun} matches.</li>
            ) : null}
            {results.map((o, i) => {
              const active = o.id === value;
              return (
                <li key={o.id}>
                  <button
                    type="button"
                    onClick={() => choose(o.id)}
                    onMouseEnter={() => setCursor(i)}
                    className={clsx(
                      'w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 transition',
                      i === cursor && 'bg-ink-soft/60',
                      active ? 'text-gold-bright' : 'text-cream',
                    )}
                    data-testid={`${testid}-option-${o.id}`}
                  >
                    <span className="truncate">{o.label}</span>
                    {active ? <Check size={12} className="shrink-0" /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
